import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Plus, FolderPlus, Radar, BellRing, Siren } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { CreateProjectDialog } from '@/pages/projects/components/CreateProjectDialog';
import { useAuth } from '@/auth/AuthProvider';
import { JourneyTimeline, type JourneyStep } from './JourneyTimeline';
import { suggestProjectName } from './suggest-project-name';
import { OnboardingCss } from './onboarding-css';
import { heroWrap, heroIcon, heroEyebrow, heroTitle, heroLede } from './onboarding-styles';

const BASE = '/services/healthcheck';

/**
 * State B — the workspace is active but has no projects. A guided "Get Started"
 * experience: a hero + the shared journey timeline. The first step (Create project)
 * is current/actionable; it really creates a project via CreateProjectDialog
 * (prefilled from the signed-in user) and jumps into the new project's console.
 */
export function GetStartedGuide() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const defaultName = suggestProjectName(user);
  const openCreate = () => setDialogOpen(true);

  const steps: JourneyStep[] = [
    {
      title: 'Create a project',
      description: 'Monitors live inside a project — your workspace for a set of endpoints or services.',
      icon: <FolderPlus size={15} />,
      actionLabel: 'Create project',
      onAction: openCreate,
    },
    {
      title: 'Add your first monitor',
      description: 'Track uptime and latency of an HTTP endpoint, host, port, DNS record or TLS certificate.',
      icon: <Radar size={15} />,
    },
    {
      title: 'Connect a notification channel',
      description: 'Send alerts to Telegram, Email, Slack, Teams or a webhook when something breaks.',
      icon: <BellRing size={15} />,
    },
    {
      title: 'Set an alarm',
      description: 'Define the condition (e.g. 3 consecutive failures) that opens an incident and notifies you.',
      icon: <Siren size={15} />,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      <OnboardingCss />

      <div className="vn-ob-hero" style={heroWrap}>
        <span className="vn-ob-halo" style={heroIcon} aria-hidden>
          <Activity size={30} />
        </span>
        <p style={heroEyebrow}>Get started</p>
        <h2 style={heroTitle}>Let&apos;s set up your monitoring</h2>
        <p style={heroLede}>
          Healthcheck watches your endpoints and services around the clock and alerts
          your team the moment something goes down. Start by creating your first project.
        </p>
        <Button size="lg" startIcon={<Plus size={16} />} onClick={openCreate} style={{ marginTop: 'var(--vn-space-2)' }}>
          Create project
        </Button>
      </div>

      <JourneyTimeline steps={steps} currentIndex={0} />

      <CreateProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultName={defaultName}
        onCreated={(projectId) => navigate(`${BASE}/${projectId}`)}
      />
    </div>
  );
}
